import { DestroyRef, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LOCAL_STORAGE } from '@core/injectors';
import { ConfigService } from '@core/services';
import { AuthenticationService } from '@features/server-side-authentication';
import { filter, finalize, interval, map, Observable, startWith, Subscription, tap } from 'rxjs';

const DEFAULT_TIMEOUT_TIME = 10 * 60 * 1000; // 10 min

@Injectable()
export class IdleTokenService {
  public readonly STORAGE_KEY = 'tku';
  private readonly REFRESH_LOCK_KEY = 'token_refresh_lock';
  private readonly LOCK_TTL = 15000;

  private readonly TIMER_INTERVAL = 10 * 1000; // 10s
  private readonly MAX_TIME_RESTORE = 30 * 60 * 1000;

  protected readonly timeoutTime: number;
  private readonly timeoutTimeShift = Math.random() * 120 * 1000; // Random shift of timer in range of 2 min

  private dateTimeStart: Date | null = null;
  private timer = 0;
  private intervalRestoreToken!: Subscription;

  constructor(
    @Inject(LOCAL_STORAGE) private readonly storage: Storage,
    private readonly destroyRef: DestroyRef,
    private readonly authenticationService: AuthenticationService,
    private readonly configService: ConfigService,
  ) {
    this.timeoutTime = this.configService.restoreTokenTime || DEFAULT_TIMEOUT_TIME;
    this.loadTimeFromStorage();
  }

  private updateTimeInStorage() {
    this.dateTimeStart = new Date();
    this.storage.setItem(this.STORAGE_KEY, this.dateTimeStart.toISOString());
  }

  public loadTimeFromStorage() {
    const activity: string | null = this.storage.getItem(this.STORAGE_KEY);

    if (activity) {
      this.dateTimeStart = new Date(activity);
      const currentTime: Date = new Date();
      const timeDiff = currentTime.getTime() - this.dateTimeStart.getTime();
      if (timeDiff >= this.timeoutTime + this.timeoutTimeShift && timeDiff < this.MAX_TIME_RESTORE) {
        this.startWatching();
      }
    }
  }

  public restoreToken(): Observable<any> {
    this.updateTimeInStorage();
    return this.authenticationService.restoreToken();
  }

  public startWatching() {
    this.updateTimeInStorage();
    this.intervalRestoreToken = interval(this.TIMER_INTERVAL)
      .pipe(
        startWith(0),
        takeUntilDestroyed(this.destroyRef),
        tap(() => (this.timer += this.TIMER_INTERVAL)),
        map(() => new Date().getTime() - (this.dateTimeStart?.getTime() || 0)),
        filter((timeDiff) => timeDiff > this.timeoutTime + this.timeoutTimeShift && timeDiff < this.MAX_TIME_RESTORE),
      )
      .subscribe(() => {
        const now = Date.now();
        const lock = this.storage.getItem(this.REFRESH_LOCK_KEY);

        if (!lock || now > parseInt(lock, 10) + this.LOCK_TTL) {
          this.storage.setItem(this.REFRESH_LOCK_KEY, now.toString());
          this.restoreToken()
            .pipe(
              finalize(() => this.storage.removeItem(this.REFRESH_LOCK_KEY)),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
              next: () => (this.timer = 0),
              error: (err) => console.error('Token refresh failed', err),
            });
        }
      });
  }

  public stopWatching() {
    this.storage.removeItem(this.STORAGE_KEY);
    this.intervalRestoreToken?.unsubscribe();
  }
}
